import { useState, useEffect, useRef } from "react";
import { FiUser, FiLock, FiEye, FiEyeOff, FiAlertCircle } from "react-icons/fi";
import { useAppNavigation } from "../../../routes/useAppNavigation";
import { useAppDispatch } from "../../../redux/store";
import { updateUserData } from "../../../redux/slices/user";
import type { User } from "../../../shared/types/auth";
import { authService } from "../../../shared/services/apis";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const { ROUTES } = useAppNavigation();
  const dispatch = useAppDispatch();
  const emailInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    emailInputRef.current?.focus();
    const savedEmail = localStorage.getItem("rememberedEmail");
    const savedPassword = localStorage.getItem("rememberedPassword");
    const wasRemembered = localStorage.getItem("rememberMe") === "true";
    if (wasRemembered && savedEmail) {
      setEmail(savedEmail);
      setRememberMe(true);
      if (savedPassword) setPassword(savedPassword);
    }
    if (authService.isAuthenticated()) {
      const user = authService.getCurrentUser();
      if (user) redirectBasedOnRole(user);
    }
  }, []);

  const redirectBasedOnRole = (user: User) => {
    if (user.role === "SUPER_ADMIN" || !user.brandId) {
      window.location.href = ROUTES.SUPER_ADMIN;
    } else {
      window.location.href = ROUTES.HOME;
    }
  };

  const handleLogin = async () => {
    if (!email || !password) return setError("Please enter both email and password.");
    setIsLoading(true);
    setError("");
    try {   
      const response = await authService.login({ email: email.trim(), password });
      if (response?.access_token && response?.user) {
        if (rememberMe) {
          localStorage.setItem("rememberedEmail", email);
          localStorage.setItem("rememberedPassword", password);
          localStorage.setItem("rememberMe", "true");
        } else {
            localStorage.removeItem("rememberedEmail");
            localStorage.removeItem("rememberedPassword");
            localStorage.removeItem("rememberMe");
        }
        dispatch(updateUserData(response));
        redirectBasedOnRole(response.user);
      } else {
        setError("Unexpected server response. Please try again.");
      }
    } catch (err: any) {
      const status = err.response?.status;
      const msg = err.response?.data?.message || err.response?.data?.error;
      const fallback = "Login failed. Please try again.";
      const statusMessages: Record<number, string> = {
        401: "Invalid email or password.",
        403: "Account deactivated. Contact support.",
        429: "Too many login attempts. Try again later.",
        500: "Server error. Please try again.",
      };
      setError(statusMessages[status] || msg || fallback);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 p-4">
      <div className="w-full max-w-md bg-white border border-gray-200 rounded-2xl shadow-lg p-8">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <img
            src="https://s3-qa-8byte.s3.ap-south-1.amazonaws.com/8Black.png"
            alt="8Byte Logo"
            className="h-14"
            onError={(e) => {
              e.currentTarget.style.display = "none";
              const fallback = e.currentTarget.nextElementSibling as HTMLElement;
              if (fallback) fallback.style.display = "flex";
            }}
          />
          <div className="hidden items-center justify-center text-black font-bold text-2xl">
            8Byte
          </div>
        </div>

        {/* Heading */}
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            Welcome Back
          </h2>
          <p className="text-sm text-gray-500">Sign in to continue to your account</p>
        </div>

        {/* Email Input */}
        <div className="mb-5">
          <label htmlFor="email" className="text-sm text-gray-700 font-medium mb-2 block">
            Email Address
          </label>
          <div className="relative">
            <FiUser className="absolute top-1/2 -translate-y-1/2 left-3.5 text-gray-400" size={18} />
            <input
              ref={emailInputRef}
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full pl-11 pr-4 py-3.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-black focus:outline-none transition-all duration-200 text-gray-900 placeholder:text-gray-400"
              placeholder="you@example.com"
              autoComplete="email"
              onKeyDown={(e) => e.key === "Enter" && handleLogin()}
            />
          </div>
        </div>

        {/* Password Input */}
        <div className="mb-5">
          <label htmlFor="password" className="text-sm text-gray-700 font-medium mb-2 block">
            Password
          </label>
          <div className="relative">
            <FiLock className="absolute top-1/2 -translate-y-1/2 left-3.5 text-gray-400" size={18} />
            <input
              type={showPassword ? "text" : "password"}
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full pl-11 pr-11 py-3.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-black focus:outline-none transition-all duration-200 text-gray-900 placeholder:text-gray-400"
              placeholder="••••••••"
              autoComplete="current-password"
              onKeyDown={(e) => e.key === "Enter" && handleLogin()}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute top-1/2 -translate-y-1/2 right-3.5 text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Toggle Password Visibility"
            >
              {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
            </button>
          </div>
        </div>

        {/* Remember Me */}
        <div className="flex items-center justify-between mb-6">
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer hover:text-gray-900 transition-colors select-none">
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-black focus:ring-2 focus:ring-black focus:ring-offset-0 cursor-pointer"
            />
            <span>Remember me</span>
          </label>
        </div>

        {/* Error Message */}
        {error && (
          <div className="flex items-center gap-2.5 text-red-700 text-sm bg-red-50 border border-red-200 p-3.5 rounded-lg mb-5 animate-in fade-in slide-in-from-top-2 duration-300">
            <FiAlertCircle size={18} className="flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Submit Button */}
        <button
          onClick={handleLogin}
          disabled={isLoading || !email || !password}
          className={`w-full py-3.5 rounded-lg text-white font-semibold text-base transition-all duration-200 ${
            isLoading || !email || !password
              ? "bg-gray-300 cursor-not-allowed"
              : "bg-black hover:bg-gray-800 hover:shadow-lg active:scale-[0.98]"
          }`}
        >
          {isLoading ? (
            <div className="flex items-center justify-center gap-2">
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
              <span>Signing in...</span>
            </div>
          ) : (
            "Sign In"
          )}
        </button>

        {/* Footer */}
        <div className="text-center mt-8 text-xs text-gray-400">
          © {new Date().getFullYear()} 8Byte.ai — All rights reserved.
        </div>
      </div>
    </div>
  );
};

export default Login;
